import { findIndex, uniq } from "lodash";

import { IQueryTableResult, TableContext } from "#services/pilotService/types";
import { tokenize } from "#/util/stringUtils";
import { immaSort } from "#/util/immaSort";

/**
 * Contains all DOI measures as well as the name of a single column
 */
export interface ParameterizedColumn {
    name: string;
    relevance: number;
    query: number;
    comparison: number;
    favorite: number;
}

/**
 * A merged similarity matrix along with the column names
 */
export interface MergedMatrix {
    matrix: number[];
    columnNames: string[];
}

/**
 * A column name and a boolean indicating whether the column can be collapsed
 */
export interface CollapseableColumn {
    name: string;
    collapseable: boolean;
    doi: number;
}

/**
 * Computes the relevance rating of a column name to the given tokenized query
 * @param columnName
 * @param queryTokens
 * @returns
 */
export function computeQueryRelevance(
    columnName: string,
    queryTokens: string[]
): number {
    if (queryTokens.length === 0) {
        return 0;
    }
    const columnTokens = tokenize(columnName);
    const scores = columnTokens.map(x => {
        if (queryTokens.includes(x)) {
            // score of full match is 1
            return 1;
        } else {
            // score of partial match is fraction of matching characters
            return Math.max(
                ...queryTokens.map(q =>
                    x.includes(q) ? q.length / x.length : 0
                )
            );
        }
    });
    // query relevance is sum of scores divided by number of words in query
    return scores.reduce((prev, curr) => prev + curr, 0) / queryTokens.length;
}

/**
 * Extracts the DOI attributes of a single column.
 * @param columnName the name of the column
 * @param relevance the relevance value of the column
 * @param context the table context
 * @param query the reference query as string or list of tokens
 * @param favColumns the favorited column names
 * @returns
 */
function computeSingleColumnAttributes(
    columnName: string,
    relevance?: number,
    context?: TableContext,
    query?: string | string[],
    favColumns?: string[]
): ParameterizedColumn {
    const queryWeight =
        query === undefined
            ? 0
            : computeQueryRelevance(
                  columnName,
                  typeof query === "string" ? tokenize(query) : query
              );
    const comparisonWeight = context?.comparisonWeights?.[columnName] ?? 0;
    const favoriteWeight = favColumns?.includes(columnName) ? 1 : 0;

    return {
        name: columnName,
        relevance: relevance ?? 0,
        query: queryWeight,
        comparison: isNaN(comparisonWeight) ? 0 : comparisonWeight,
        favorite: favoriteWeight,
    };
}

/**
 * Extracts the DOI attributes of all columns of the given table.
 * @param table the table
 * @param context the table context
 * @param query the reference query as string or list of tokens
 * @param favColumns the favorited column names
 * @returns
 */
export function computeTableColumnAttributes(
    table: IQueryTableResult,
    context?: TableContext,
    query?: string | string[],
    favColumns?: string[]
): ParameterizedColumn[] {
    const q = typeof query === "string" ? tokenize(query) : query;
    return table.columns.map((c, i) =>
        computeSingleColumnAttributes(
            c,
            table.meta.relevancies?.[i],
            context,
            q,
            favColumns
        )
    );
}

/**
 * Extracts the DOI attributes of all unique columns of the given table.
 * Uses the maximum values for column names that occur in multiple tables.
 * @param tables the list of tables
 * @param context the table context
 * @param query the reference query
 * @param favColumns the favorited column names
 * @returns
 */
export function computeColumnAttributes(
    tables: IQueryTableResult[],
    context?: TableContext,
    query?: string,
    favColumns?: string[]
): ParameterizedColumn[][] {
    const q = query !== undefined ? tokenize(query) : undefined;
    return tables.map(t =>
        computeTableColumnAttributes(t, context, q, favColumns)
    );
}

/**
 * Merges groups of column attributes with the same column names by picking their largest values.
 * @param columnAttributes a list of table-wise column attributes
 * @returns the merged list of column attributes for all column names
 */
export function mergeColumnAttributes(
    columnAttributes: ParameterizedColumn[][]
): ParameterizedColumn[] {
    const allColumns = columnAttributes.flat();
    const mergedResults: ParameterizedColumn[] = [];
    for (let i = 0; i < allColumns.length; ++i) {
        const foundIndex = findIndex(
            mergedResults,
            r => r.name === allColumns[i].name
        );
        if (foundIndex === -1) {
            mergedResults.push(allColumns[i]);
        } else {
            mergedResults[foundIndex] = {
                name: allColumns[i].name,
                relevance: Math.max(
                    mergedResults[foundIndex].relevance,
                    allColumns[i].relevance
                ),
                query: Math.max(
                    mergedResults[foundIndex].query,
                    allColumns[i].query
                ),
                comparison: Math.max(
                    mergedResults[foundIndex].comparison,
                    allColumns[i].comparison
                ),
                favorite: Math.max(
                    mergedResults[foundIndex].favorite,
                    allColumns[i].favorite
                ),
            };
        }
    }
    return mergedResults;
}

/**
 * Computes the DOI for the given column with the given weights.
 * @param column the column, including its sorting attributes
 * @param doiWeights the DOI weights
 * @returns
 */
export function computeColumnDoi(
    column: ParameterizedColumn,
    doiWeights: number[]
): number {
    return (
        doiWeights[0] * column.relevance +
        doiWeights[1] * column.comparison +
        doiWeights[2] * column.query +
        doiWeights[3] * column.favorite
    );
}

/**
 * Computes the DOI of a single column based on the given measures
 * @param relevance the relevance score of the column
 * @param comparisonWeights the comparison weight map of the table
 * @param queryTokens the string tokens of the reference query
 * @param columnName the name of the column
 * @param favColumns the names of the favorited columns
 * @param doiWeights the weights for the DOI computation
 * @returns
 */
function computeDoi(
    relevance: number,
    comparisonWeights: {
        [key: string]: number;
    },
    queryTokens: string[],
    columnName: string,
    favColumns: string[],
    doiWeights: number[]
): number {
    const comparisonWeight = comparisonWeights[columnName] || 0;
    const queryWeight = computeQueryRelevance(columnName, queryTokens);
    const favoriteWeight = favColumns.includes(columnName) ? 1 : 0;
    return (
        doiWeights[0] * relevance +
        doiWeights[1] * comparisonWeight +
        doiWeights[2] * queryWeight +
        doiWeights[3] * favoriteWeight
    );
}

/**
 * Computes the DOI of the columns of the given table.
 * @param table the table
 * @param doiWeights the weights of the individual DOI terms
 * @param comparisonWeights the comparison relevance measure for the individual column names
 * @param referenceQuery the reference query
 * @param favColumns the column names favorited by the user
 * @returns
 */
export function computeTableDoi(
    table: IQueryTableResult,
    doiWeights: number[],
    comparisonWeights?: {
        [key: string]: number;
    },
    referenceQuery?: string,
    favColumns?: string[]
): number[] {
    const doi = new Array<number>(table.columns.length).fill(0);

    const queryTokens = referenceQuery ? tokenize(referenceQuery) : [];

    // make sure the relevancy scores are properly defined and the arrays are of correct length
    const relevanceArray: number[] | undefined =
        table.meta.relevancies !== undefined &&
        table.meta.relevancies.length === table.columns.length
            ? table.meta.relevancies
            : undefined;

    for (let i = 0; i < table.columns.length; ++i) {
        doi[i] = computeDoi(
            relevanceArray ? relevanceArray[i] : 0,
            comparisonWeights || {},
            queryTokens,
            table.columns[i],
            favColumns || [],
            doiWeights
        );
    }

    return doi;
}

/**
 * Computes the permutation of the columns of the given table according to ascending DOI order.
 * @param table the table
 * @param doiWeights the weights of the individual DOI terms
 * @param comparisonWeights the comparison relevance measure for the individual column names
 * @param query the reference query
 * @param favColumn the column names favorited by the user
 * @returns
 */
function computeTablePermutation(
    table: IQueryTableResult,
    doiWeights: number[],
    comparisonWeights?: {
        [key: string]: number;
    },
    query?: string,
    favColumn?: string[]
): number[] {
    const permutation = Array.from(Array(table.columns.length).keys());
    const doi = computeTableDoi(
        table,
        doiWeights,
        comparisonWeights,
        query,
        favColumn
    );
    permutation.sort((a, b) => doi[a] - doi[b]);
    return permutation;
}

/**
 * Permutes the columns of a table according to the given permutation
 * @param table
 * @param permutation
 * @returns a permuted copy of the given table
 */
function permuteTable(
    table: IQueryTableResult,
    permutation: number[]
): IQueryTableResult {
    if (permutation.length !== table.columns.length) {
        return table;
    }

    function permuteArray<T>(
        array: T[] | undefined,
        permutation: number[]
    ): T[] | undefined {
        if (array) {
            return permutation.map(x => array[x]);
        } else {
            return undefined;
        }
    }

    function permuteMatrix(
        matrix: number[] | undefined,
        permutation: number[]
    ): number[] | undefined {
        if (matrix) {
            const edgeLength = Math.sqrt(matrix.length);
            if (Math.floor(edgeLength) !== edgeLength) {
                console.warn(
                    "Redundancy Matrix not rectangular. Can not permute."
                );
                return undefined;
            } else {
                // permuted array of column indices
                const cols = Array.from(Array(edgeLength).keys());
                // fill permuted columns with values from permuted rows
                const permutedMatrix = permutation
                    .map(x => cols[x])
                    .flatMap(c =>
                        permutation.map(r => matrix[c * edgeLength + r])
                    );
                return permutedMatrix;
            }
        } else {
            return undefined;
        }
    }

    return {
        columns: permutation.map(x => table.columns[x]),
        data: table.data.map(row => permutation.map(x => row[x])),
        type: table.type,
        sourceQuery: table.sourceQuery,
        queryType: table.queryType,
        interpretationId: table.interpretationId,
        tableId: table.tableId,
        meta: {
            descriptors: permuteArray(table.meta.descriptors, permutation),
            types: permuteArray(table.meta.types, permutation),
            columns: permuteArray(table.meta.columns, permutation),
            labels: permuteArray(table.meta.labels, permutation),
            name: table.meta.name,
            relevancies: permuteArray(table.meta.relevancies, permutation),
            redundancies: permuteArray(
                table.meta.prefixSimilarityMatrix,
                permutation
            ),
            redundancyMatrix: permuteMatrix(
                table.meta.redundancyMatrix,
                permutation
            ),
        },
    };
}

/**
 * Computes the DOI of the given tables' columns and permutes the columns in ascending DOI order.
 * @param tables the tables
 * @param context the table context
 * @param favColumns the column names favorited by the user
 * @param query the reference query
 * @param weights the weights of the individual DOI terms
 * @returns a set of copies of the original tables with permuted columns
 */
export function getRelevancyAdjustedTables(
    tables: IQueryTableResult[],
    weights: number[],
    context: TableContext | undefined,
    query?: string,
    favColumns?: string[]
): IQueryTableResult[] {
    if (weights.length !== 5) {
        console.warn(
            "Could not compute DOI relevancies: invalid weight settings."
        );
        return tables;
    }

    const resultTables: IQueryTableResult[] = [];

    for (const t of tables) {
        const permutation = computeTablePermutation(
            t,
            weights,
            context?.comparisonWeights,
            query,
            favColumns
        );
        const permutedTable = permuteTable(t, permutation);
        resultTables.push(permutedTable);
    }

    return resultTables;
}

// internal representation of either clusters or single columns, used for high level ordering
type HighLevelElement = ClusterElement | ColumnElement;

// internal representation of a cluster, used for high level ordering
interface ClusterElement {
    id: number;
    doi: number;
    type: "cluster";
}

// internal representation of a single column, used for high as well as low level ordering
interface ColumnElement {
    id: string;
    doi: number;
    collapseable: boolean;
    type: "col";
}

/**
 * Computes the ordering of the given columns.
 * Supports three different modes:
 *   * If a clustering is given, the order is computed hierarchically.
 *     The clusters and noise columns (clusterId = -1) of the given clustering are ordered by their DOI.
 *     Cluster DOI is computed from the max-union of the column attributes.
 *     Columns within clusters are then ordered by their DOI.
 *     In this mode, the redundancy term of the DOI computation is ignored.
 *   * If no clustering is given and the method is set to "DOI" (default), all columns are ordered by their DOI.
 *   * If no clustering is given and the method is set to "ImmaSort", the columns are ordered with the ImmaSort algorithm.
 * @param cols the sorting attributes of the columns to be sorted
 * @param weights the weights of the individual column attributes
 * @param clustering an optional list of cluster indices for each column
 * @param method the clustering method to be used if no clustering is given
 * @param preferredNumberOfVisibleColumns configures auto collapsing of columns
 * @param mergeUnclusteredItemsIntoCluster configures if all "lonely" columns should be merged into a single cluster although they are not similar to each other.
 * @returns an ordered list of column names with an additional flag indicating whether the individual column may be collapsed.
 */
export function getColumnSortOrder(
    cols: ParameterizedColumn[],
    weights: number[],
    clustering?: number[],
    method: "ImmaSort" | "DOI" = "DOI",
    preferredNumberOfVisibleColumns: number = 18,
    mergeUnclusteredItemsIntoCluster: boolean = false
): CollapseableColumn[] {
    if (clustering === undefined) {
        if (method === "ImmaSort") {
            // sort columns with imma sort
            const indexArray = immaSort(
                cols.map(x => [x.relevance, x.comparison, x.query, x.favorite]),
                [-1, -1, -1, -1],
                weights,
                0.01 * cols.length
            );
            return indexArray.map(x => {
                return {
                    name: cols[x].name,
                    collapseable: false,
                    doi: computeColumnDoi(cols[x], weights),
                };
            });
        } else {
            // sort columns according to their DOI
            return cols
                .sort(
                    (a, b) =>
                        computeColumnDoi(b, weights) -
                        computeColumnDoi(a, weights)
                )
                .map(x => {
                    return {
                        name: x.name,
                        collapseable: false,
                        doi: computeColumnDoi(x, weights),
                    };
                });
        }
    } else {
        if (cols.length === 0) {
            return [];
        }

        if (cols.length !== clustering.length) {
            throw Error("cols and clustering arrays must have same size");
        }
        // compute column order hierarchically

        // the available set of clusters is called uniqueClusters
        // always use the value of the uniqueCluster, NOT the index.
        const uniqueClustersRaw = uniq(clustering);
        const uniqueClusters = uniqueClustersRaw.filter(
            d => mergeUnclusteredItemsIntoCluster || d !== -1
        );

        // aggregate metrics for each cluster
        const clusterMetrics: ParameterizedColumn[] = Array(
            uniqueClusters.length
        );
        for (let i = 0; i < uniqueClusters.length; ++i) {
            clusterMetrics[i] = {
                name: uniqueClusters[i].toString(),
                relevance: 0,
                comparison: 0,
                query: 0,
                favorite: 0,
            };
            // max-union of all metrics that belong to this cluster
            for (let j = 0; j < clustering.length; ++j) {
                if (clustering[j] === uniqueClusters[i]) {
                    clusterMetrics[i] = {
                        ...clusterMetrics[i],
                        relevance: Math.max(
                            clusterMetrics[i].relevance,
                            cols[j].relevance
                        ),
                        comparison: Math.max(
                            clusterMetrics[i].comparison,
                            cols[j].comparison
                        ),
                        query: Math.max(clusterMetrics[i].query, cols[j].query),
                        favorite: Math.max(
                            clusterMetrics[i].favorite,
                            cols[j].favorite
                        ),
                    };
                }
            }
        }
        // compute cluster doi
        const clusterDoi = clusterMetrics.map(d =>
            computeColumnDoi(d, weights)
        );
        // compute doi of all columns
        const elementDoi = cols.map(d => computeColumnDoi(d, weights));

        // build high level objects (all clusters and all columns that have not been assigned to a cluster)
        const highLevel: HighLevelElement[] = [
            ...cols
                .map((d, i) => {
                    return {
                        id: d.name,
                        doi: elementDoi[i],
                        collapseable: false,
                        type: "col",
                    } as ColumnElement;
                })
                .filter((x, i) => {
                    return mergeUnclusteredItemsIntoCluster
                        ? false
                        : clustering[i] === -1;
                }),
            ...uniqueClusters.map((d, i) => {
                return {
                    id: d,
                    doi: clusterDoi[i],
                    type: "cluster",
                } as ClusterElement;
            }),
        ];
        highLevel.sort((a, b) => b.doi - a.doi);

        // compute order of all columns within clusters
        const clusterSorting: ColumnElement[][] = [];
        for (let i = 0; i < uniqueClusters.length; ++i) {
            const cluster = uniqueClusters[i];
            const elements = cols
                .map((d, i) => {
                    return {
                        id: d.name,
                        doi: elementDoi[i],
                        collapseable: true,
                        type: "col",
                    } as ColumnElement;
                })
                .filter((d, i) => clustering[i] === cluster);
            elements.sort((a, b) => b.doi - a.doi);
            // the first (highest DOI) column of each cluster shall not be collapsed and act as representative for the cluster
            elements[0].collapseable = false;
            clusterSorting.push(elements);
        }

        /**
         * Now we define the actually visible columns. In cases, we have only very few clusters (e.g. 2), and waste screen space if we only show 2 representative columns. Therefore, we add more columns to the result; namely those, that carry a high doi.
         * If a cluster contains multiple high-doi columns, we want to show more than one column for this cluster -
         * we therefore "switch on" additional columns, until the preferrable column count has been reached
         */
        const columnElements = clusterSorting.flatMap(x => x);
        let currentlyVisibleColumns = columnElements.filter(
            x => !x.collapseable
        ).length;
        columnElements.sort((a, b) => b.doi - a.doi);
        for (const columnElement of columnElements) {
            // iterate over all columns, by decreasing DOI.
            if (currentlyVisibleColumns >= preferredNumberOfVisibleColumns) {
                break;
            }
            // if the current element is already visible, then just skip it, otherwise make it visible and increase counter
            if (columnElement.collapseable) {
                columnElement.collapseable = false;
                currentlyVisibleColumns++;
            }
        }

        // create global order by replacing all clusters in the high level order with their respective list of sorted columns
        const result: CollapseableColumn[] = [];
        for (let i = 0; i < highLevel.length; ++i) {
            const element = highLevel[i];
            if (element.type === "col") {
                // copy column
                result.push({
                    name: element.id,
                    collapseable: element.collapseable,
                    doi: element.doi,
                });
            } else {
                // replace cluster element with the corresponding columns
                const clusterId = element.id;
                const index = findIndex(uniqueClusters, x => x === clusterId);
                if (index < 0 || index >= uniqueClusters.length) {
                    // continue;
                } else {
                    result.push(
                        ...clusterSorting[index].map(x => {
                            return {
                                name: x.id,
                                collapseable: x.collapseable,
                                doi: x.doi,
                            };
                        })
                    );
                }
            }
        }

        return result;
    }
}

/**
 * merges the redundancy matrices of the given tables into one matrix.
 * @param tables the tables
 * @returns the merged redundancy matrix, as well as the ordered list of column names associated with the merged matrix's columns.
 */
export function mergeRedundancyMatrices(
    tables: IQueryTableResult[]
): MergedMatrix {
    const tgtCols = uniq(tables.flatMap(t => t.columns));
    // initialize all similarities to zero
    const tgtMatrix = Array<number>(tgtCols.length * tgtCols.length).fill(0);

    for (let tgtI = 0; tgtI < tgtCols.length; ++tgtI) {
        for (let tgtJ = tgtI; tgtJ < tgtCols.length; ++tgtJ) {
            if (tgtI === tgtJ) {
                // set diagonal to 0
                tgtMatrix[tgtJ + tgtI * tgtCols.length] = 0;
            } else {
                // collect similarity values for this pair of columns from all tables
                const validWeights: number[] = [];
                for (const t of tables) {
                    const srcI = findIndex(
                        t.columns,
                        name => name === tgtCols[tgtI]
                    );
                    const srcJ = findIndex(
                        t.columns,
                        name => name === tgtCols[tgtJ]
                    );
                    if (
                        srcI !== -1 &&
                        srcJ !== -1 &&
                        t.meta.redundancyMatrix !== undefined
                    ) {
                        validWeights.push(
                            t.meta.redundancyMatrix[
                                srcJ + srcI * t.columns.length
                            ]
                        );
                    }
                }
                // use maximum similarity for merged matrix
                if (validWeights.length > 0) {
                    tgtMatrix[tgtJ + tgtI * tgtCols.length] = Math.min(
                        ...validWeights
                    );
                    tgtMatrix[tgtI + tgtJ * tgtCols.length] = Math.min(
                        ...validWeights
                    );
                }
            }
        }
    }

    return {
        matrix: tgtMatrix,
        columnNames: tgtCols,
    };
}

/**
 * Performs DBSCAN clustering on the entries of a given similarity matrix.
 * @param matrix the matrix
 * @param maxDist the maximum neighborhood size around a point
 * @param minPts the minimum number of points required in a neighborhood to be considered a cluster
 * @returns a list of cluster indices for each column of the given similarity matrix, -1 is for columns that did not find a cluster size > 0
 */
export function clusterRedundancyMatrix(
    matrix: number[],
    maxDist: number,
    minPts: number
): number[] {
    const numCols = Math.sqrt(matrix.length);
    const clusterIndices = Array<number>(numCols).fill(-1);
    const toAssign = Array<boolean>(numCols).fill(true);
    let nextCluster = 0;

    while (toAssign.some(t => t)) {
        // pick a seed point that has not yet been assigned
        const unassigned: [boolean, number][] = toAssign
            .map((d, i) => [d, i] as [boolean, number])
            .filter(d => d[0]);
        const seedIndex =
            unassigned[Math.floor(Math.random() * unassigned.length)][1];

        // create queue of points to visit
        const toVisit: number[] = [seedIndex];

        // visit points in queue
        for (let v = 0; v < toVisit.length; ++v) {
            const focus = toVisit[v];

            // the current point will not be used as seed point in the future
            toAssign[focus] = false;

            // find neighboring point indices
            const neighbors = matrix
                .slice(numCols * focus, numCols * (focus + 1))
                .map((d, i) => [d, i])
                .filter(d => 1 - d[0] <= maxDist && d[1] !== focus)
                .map(d => d[1]);

            // assign cluster indices
            if (neighbors.length >= minPts - 1) {
                // determine the other clusters that can be found in this neighborhood
                const neighborhoodClusters = uniq(
                    neighbors.map(d => clusterIndices[d]).filter(d => d !== -1)
                );
                const focusCluster = clusterIndices[focus];
                if (
                    focusCluster !== -1 &&
                    !neighborhoodClusters.includes(focusCluster)
                ) {
                    // add cluster of current point to list of clusters in this neighborhood
                    neighborhoodClusters.push(clusterIndices[focus]);
                }

                let currentCluster: number;
                if (neighborhoodClusters.length === 1) {
                    // append the whole neighborhood to the same cluster
                    currentCluster = neighborhoodClusters[0];
                } else {
                    // start a new cluster
                    currentCluster = nextCluster;
                    ++nextCluster;
                }

                // set cluster indices and add all neighbors that have yet to be visited to the queue
                clusterIndices[focus] = currentCluster;
                for (let n = 0; n < neighbors.length; ++n) {
                    clusterIndices[neighbors[n]] = currentCluster;
                    if (
                        toAssign[neighbors[n]] &&
                        !toVisit.includes(neighbors[n])
                    ) {
                        toVisit.push(neighbors[n]);
                    }
                }

                if (neighborhoodClusters.length > 1) {
                    // merge all other clusters found within the neighborhood into the new one
                    for (let i = 0; i < clusterIndices.length; ++i) {
                        if (neighborhoodClusters.includes(clusterIndices[i])) {
                            clusterIndices[i] = currentCluster;
                        }
                    }
                }
            }
        }
    }

    return clusterIndices;
}

/**
 * Sorts the given column attributes in the same order as the given list of column names.
 * @param columnAttributes the column attributes
 * @param columnOrder the column names
 * @returns a reordered copy of the given column attributes
 */
export function alignColumnAttributes(
    columnAttributes: ParameterizedColumn[],
    columnOrder: string[]
): ParameterizedColumn[] {
    const result: ParameterizedColumn[] = [];
    for (let i = 0; i < columnOrder.length; ++i) {
        const foundIndex = findIndex(
            columnAttributes,
            d => d.name === columnOrder[i]
        );
        if (foundIndex !== -1) {
            result.push(columnAttributes[foundIndex]);
        }
    }
    return result;
}
